/**
 * Fallback artwork shown for products that have no usable photo.
 *
 * Design notes:
 * - **Transparent background on purpose.** Every container that can show this
 *   (`.product-img-wrap`, `.detail-main-img`, `.detail-thumb`, `.search-result-img`)
 *   already paints the same cream gradient, so the mark sits on it natively and we
 *   never have to match a background colour per context.
 * - **Rendered with `object-fit: contain`** (see `.img-placeholder` in App.css) rather
 *   than the `cover` used for real photos, so the mark is never cropped — the same
 *   asset works in a 3:4 card, a 3:4 detail hero, an 80px square thumb and a 56px
 *   search row.
 * - **Explicit `width`/`height` are required**, not just a viewBox. Without intrinsic
 *   dimensions an SVG in an `<img>` has no intrinsic size, and `object-fit` scales it
 *   against the browser's default replaced-element size instead of the artwork.
 * - **No nested quotes** (e.g. `font-family="Georgia, serif"`, not `'Times New Roman'`)
 *   to keep the data URI robust.
 * - The viewBox is cropped to the artwork rather than a square canvas, so the mark
 *   fills a portrait card properly instead of floating small in the middle.
 *
 * Built once at module load and shared by every card — it is the same for all
 * products, so there is no per-product string to allocate.
 */

const GOLD = "#c9a96e";

const WATCH_PLACEHOLDER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="280" height="420" viewBox="60 0 280 420" fill="none" role="img" aria-label="No image available">
  <g stroke="${GOLD}" stroke-linecap="round" stroke-linejoin="round">
    <path d="M172 128 L176 52 A8 8 0 0 1 184 44 L216 44 A8 8 0 0 1 224 52 L228 128" stroke-width="2" opacity=".55"/>
    <path d="M172 252 L176 328 A8 8 0 0 0 184 336 L216 336 A8 8 0 0 0 224 328 L228 252" stroke-width="2" opacity=".55"/>
    <line x1="177" y1="86" x2="223" y2="86" stroke-width="1.2" opacity=".3"/>
    <line x1="177" y1="104" x2="223" y2="104" stroke-width="1.2" opacity=".3"/>
    <line x1="177" y1="294" x2="223" y2="294" stroke-width="1.2" opacity=".3"/>
    <path d="M270 180 L282 180 L282 200 L270 200" stroke-width="2" opacity=".7"/>
    <circle cx="200" cy="190" r="72" stroke-width="2.5" opacity=".9"/>
    <circle cx="200" cy="190" r="60" stroke-width="1" opacity=".4"/>
    <line x1="200" y1="140" x2="200" y2="150" stroke-width="2" opacity=".55"/>
    <line x1="250" y1="190" x2="240" y2="190" stroke-width="2" opacity=".55"/>
    <line x1="200" y1="240" x2="200" y2="230" stroke-width="2" opacity=".55"/>
    <line x1="150" y1="190" x2="160" y2="190" stroke-width="2" opacity=".55"/>
    <line x1="200" y1="190" x2="238" y2="168" stroke-width="2.5" opacity=".8"/>
    <line x1="200" y1="190" x2="174" y2="175" stroke-width="2.5" opacity=".8"/>
    <circle cx="200" cy="190" r="3.5" fill="${GOLD}" stroke="none" opacity=".9"/>
  </g>
  <line x1="170" y1="352" x2="230" y2="352" stroke="${GOLD}" stroke-width="1" opacity=".5"/>
  <text x="200" y="380" text-anchor="middle" font-family="Georgia, serif" font-size="17" letter-spacing="2.4" fill="${GOLD}" opacity=".75">Image coming soon</text>
</svg>`;

/** Inline data URI — cannot 404, needs no request, identical for every product. */
export const WATCH_PLACEHOLDER_IMG = `data:image/svg+xml,${encodeURIComponent(
  WATCH_PLACEHOLDER_SVG,
)}`;

export default WATCH_PLACEHOLDER_IMG;
